import { Filter, GenericFilter } from "data/@types/GlobalSearchData";
import { AdvancedViewFilterType } from "data/store/global-search/AdvancedViewFiltersStore";
import { operatorToOperation } from "./filterOperators";

export const buildTextFilter = (
  field: string,
  filterObj: AdvancedViewFilterType,
): GenericFilter | null => {
  const op = filterObj.operator ?? "contains";
  if (op === "is_empty") return { field, operation: "NOT_EXISTS" };
  if (op === "is_not_empty") return { field, operation: "EXISTS" };
  const raw =
    typeof filterObj.valueText === "string" ? filterObj.valueText.trim() : "";
  if (!raw) return null;
  const operation = operatorToOperation(op);
  const value =
    (operation === "EQUALS" || operation === "NOT_EQUALS") && /^\d+$/.test(raw)
      ? parseInt(raw, 10)
      : raw;
  return { field, operation, value };
};

export const buildDateRangeFilter = (
  field: string,
  filterObj: AdvancedViewFilterType,
): GenericFilter | null => {
  const { from, to } = filterObj.valueDateRange ?? {};
  if (!from && !to) return null;
  return {
    field,
    operation: "BETWEEN",
    value: {
      ...(from && { from: from.toISOString().slice(0, 23) }),
      ...(to && { to: to.toISOString().slice(0, 23) }),
      format: "yyyy-MM-dd'T'HH:mm:ss.SSS",
    },
  };
};

export const buildNumericFilter = (
  field: string,
  filterObj: AdvancedViewFilterType,
): GenericFilter | null => {
  const op = filterObj.operator ?? "equals";
  const raw =
    typeof filterObj.valueText === "string" ? filterObj.valueText.trim() : "";
  if (!raw) return null;

  if (op === "in") {
    const ids = raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => /^\d+$/.test(s))
      .map((s) => parseInt(s, 10));
    if (!ids.length) return null;
    return { field, operation: "IN", value: ids };
  }

  if (!/^\d+$/.test(raw)) return null;
  const value = parseInt(raw, 10);
  if (op === "equals") return { field, operation: "EQUALS", value };
  if (op === "does_not_equal") return { field, operation: "NOT_EQUALS", value };
  return null;
};

export const buildBooleanFilter = (
  field: string,
  filterObj: AdvancedViewFilterType,
  invertValue = false,
): GenericFilter | null => {
  const val = filterObj.valueText;
  if (!val || val === "any") return null;
  const boolValue = val === "true";
  return {
    field,
    operation: "EQUALS",
    value: invertValue ? !boolValue : boolValue,
  };
};

export const buildSelectFilter = (
  field: string,
  filterObj: AdvancedViewFilterType,
): GenericFilter | null => {
  const values = Array.isArray(filterObj.valueText)
    ? filterObj.valueText.filter(Boolean)
    : typeof filterObj.valueText === "string" && filterObj.valueText.trim()
      ? [filterObj.valueText.trim()]
      : [];
  if (!values.length) return null;
  return { field, operation: "IN", value: values };
};

export const buildGenericFilter = (
  filterMeta: Filter,
  filterObj: AdvancedViewFilterType | undefined,
): GenericFilter | null => {
  if (!filterObj) return null;
  const { field, type } = filterMeta;
  switch (type) {
    case "text":
      return buildTextFilter(field, filterObj);
    case "numeric":
      return buildNumericFilter(field, filterObj);
    case "daterange":
      return buildDateRangeFilter(field, filterObj);
    case "boolean":
      return buildBooleanFilter(field, filterObj, filterMeta.invertValue);
    case "select":
    case "simple-select":
    case "checkbox-options":
      return buildSelectFilter(field, filterObj);
    default:
      return null;
  }
};
